'use client';

import React from 'react';
import styled, { css } from 'styled-components';

export type MapClusterTone = 'default' | 'info' | 'success' | 'warn' | 'error';

export interface MapClusterProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'role'> {
  /** Number of grouped points represented by the cluster. */
  count: number;
  /** Diameter in px. Default 40. */
  size?: number;
  /** Tone — drives ring colour. Defaults to `default` (brand). */
  tone?: MapClusterTone;
  /** Click handler — promotes the element to `role="button"`. */
  onClick?: () => void;
}

const Bubble = styled.div<{ $size: number; $tone: MapClusterTone; $clickable: boolean }>`
  width: ${(p) => p.$size}px;
  height: ${(p) => p.$size}px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: ${(p) => p.theme.fontFamily.mono};
  font-weight: ${(p) => p.theme.fontWeight.bold};
  color: #ffffff;
  box-shadow: ${(p) => p.theme.elevation.raised.shadow};
  cursor: ${(p) => (p.$clickable ? 'pointer' : 'default')};
  transition: transform 0.15s ease;
  ${(p) => toneStyle(p.$tone, p.theme)}

  &:hover {
    transform: ${(p) => (p.$clickable ? 'scale(1.06)' : 'none')};
  }
`;

function toneStyle(tone: MapClusterTone, theme: any) {
  if (tone === 'error')
    return css`
      background: ${theme.colors.semantic.error.color};
      border: 3px solid #ffffff;
    `;
  if (tone === 'warn')
    return css`
      background: ${theme.colors.semantic.warn.color};
      border: 3px solid #ffffff;
    `;
  if (tone === 'success')
    return css`
      background: ${theme.colors.semantic.success.color};
      border: 3px solid #ffffff;
    `;
  if (tone === 'info')
    return css`
      background: ${theme.colors.semantic.info.color};
      border: 3px solid #ffffff;
    `;
  return css`
    background: ${theme.colors.brand.primary};
    border: 3px solid #ffffff;
  `;
}

/**
 * Circular cluster marker for map overlays. Used by clustering libraries
 * (Supercluster etc.) to represent grouped points; click handler typically
 * triggers a zoom/expand on the underlying map.
 */
export function MapCluster({
  count,
  size = 40,
  tone = 'default',
  onClick,
  ...rest
}: MapClusterProps) {
  const interactive = typeof onClick === 'function';
  return (
    <Bubble
      $size={size}
      $tone={tone}
      $clickable={interactive}
      onClick={onClick}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-label={interactive ? `Cluster of ${count} — click to expand` : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      {...rest}
    >
      {count}
    </Bubble>
  );
}
